import { createClient } from '@/lib/supabase/client'
import type { CustomCategory } from '@/lib/types'

/**
 * Fetch all categories for a user (optionally filtered by type)
 */
export async function fetchUserCategories(
  userId: string,
  type?: 'expense' | 'income'
): Promise<CustomCategory[]> {
  const supabase = createClient()

  let query = supabase
    .from('user_categories')
    .select('*')
    .eq('user_id', userId)
    .order('is_default', { ascending: false })
    .order('created_at', { ascending: true })

  if (type) {
    query = query.eq('type', type)
  }

  const { data, error } = await query

  if (error) {
    console.error('Error fetching categories:', error)
    throw new Error('Failed to fetch categories')
  }

  // Transform database records to application types
  return data.map((category) => ({
    id: category.id,
    name: category.name,
    type: category.type as 'expense' | 'income',
    icon: category.icon,
    color: category.color,
    keywords: category.keywords || [],
    isDefault: category.is_default,
  }))
}

/**
 * Create a new custom category
 */
export async function createCategory(
  userId: string,
  category: Omit<CustomCategory, 'id' | 'isDefault'>
): Promise<CustomCategory> {
  const supabase = createClient()

  const { data, error} = await supabase
    .from('user_categories')
    .insert({
      user_id: userId,
      name: category.name,
      type: category.type,
      icon: category.icon,
      color: category.color,
      keywords: category.keywords,
      is_default: false,
    })
    .select()
    .single()

  if (error) {
    console.error('Error creating category:', error)

    // Check for unique constraint violation
    if (error.code === '23505') {
      throw new Error(`Category "${category.name}" already exists for ${category.type}`)
    }

    throw new Error('Failed to create category')
  }

  return {
    id: data.id,
    name: data.name,
    type: data.type as 'expense' | 'income',
    icon: data.icon,
    color: data.color,
    keywords: data.keywords || [],
    isDefault: data.is_default,
  }
}

/**
 * Update an existing custom category (non-default only)
 */
export async function updateCategory(
  categoryId: string,
  updates: Partial<Omit<CustomCategory, 'id' | 'isDefault'>>
): Promise<CustomCategory> {
  const supabase = createClient()

  const updateData: any = {}
  if (updates.name !== undefined) updateData.name = updates.name
  if (updates.type !== undefined) updateData.type = updates.type
  if (updates.icon !== undefined) updateData.icon = updates.icon
  if (updates.color !== undefined) updateData.color = updates.color
  if (updates.keywords !== undefined) updateData.keywords = updates.keywords

  const { data, error } = await supabase
    .from('user_categories')
    .update(updateData)
    .eq('id', categoryId)
    .eq('is_default', false) // Only update non-default categories
    .select()
    .single()

  if (error) {
    console.error('Error updating category:', error)

    // Check for unique constraint violation
    if (error.code === '23505') {
      throw new Error(`Category name "${updates.name}" already exists`)
    }

    throw new Error('Failed to update category')
  }

  return {
    id: data.id,
    name: data.name,
    type: data.type as 'expense' | 'income',
    icon: data.icon,
    color: data.color,
    keywords: data.keywords || [],
    isDefault: data.is_default,
  }
}

/**
 * Delete a custom category (non-default only)
 */
export async function deleteCategory(categoryId: string): Promise<void> {
  const supabase = createClient()

  const { error } = await supabase
    .from('user_categories')
    .delete()
    .eq('id', categoryId)
    .eq('is_default', false) // Only delete non-default categories

  if (error) {
    console.error('Error deleting category:', error)
    throw new Error('Failed to delete category')
  }
}

/**
 * Get category by name and type
 */
export async function getCategoryByName(
  userId: string,
  name: string,
  type: 'expense' | 'income'
): Promise<CustomCategory | null> {
  const supabase = createClient()

  const { data, error } = await supabase
    .from('user_categories')
    .select('*')
    .eq('user_id', userId)
    .eq('name', name)
    .eq('type', type)
    .single()

  if (error) {
    if (error.code === 'PGRST116') {
      // No rows returned
      return null
    }
    console.error('Error fetching category:', error)
    throw new Error('Failed to fetch category')
  }

  return {
    id: data.id,
    name: data.name,
    type: data.type as 'expense' | 'income',
    icon: data.icon,
    color: data.color,
    keywords: data.keywords || [],
    isDefault: data.is_default,
  }
}
